import { useEffect, useState, useCallback, useRef } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { noteAPI } from '../lib/api';
import { useLiveInvalidate } from '../lib/useLiveData';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { NotionEditor } from '../components/NotionEditor';
import ShareWithControl from '../components/ShareWithControl';
import { Plus, FileText, Trash2, Clock, Lock, Loader2, Sparkles, Languages, Mic, Square, Circle, ChevronLeft, Users } from 'lucide-react';
import { toast } from 'sonner';
import { formatTaskDate, formatDateLong } from '../lib/dateFormat';

export default function NotesPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  // D74 — Notes shared WITH the current user by other members
  const [sharedNotes, setSharedNotes] = useState([]);
  const [tab, setTab] = useState('mine'); // 'mine' | 'shared'
  const [loading, setLoading] = useState(true);
  const [activeNote, setActiveNote] = useState(null);
  const [loadingNote, setLoadingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [saveTimer, setSaveTimerState] = useState(null);

  // Recording state
  const [showRecorder, setShowRecorder] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [summaryLang, setSummaryLang] = useState('english');
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const fetchNotes = useCallback(({ silent = false } = {}) => {
    if (!currentWorkspace?.id) return;
    // D88+++ (2026-06-04): `silent=true` is used by the D84 WS invalidate so
    // background pushes from teammates don't flash the page-level loading
    // skeleton on every keystroke they make.
    if (!silent) setLoading(true);
    Promise.all([
      noteAPI.list(currentWorkspace.id).then(r => r.data).catch(() => []),
      // D74 — Notes others have shared with me. May 404 on old backends → empty.
      noteAPI.listShared(currentWorkspace.id).then(r => r.data).catch(() => []),
    ]).then(([mine, shared]) => {
      setNotes(Array.isArray(mine) ? mine : []);
      setSharedNotes(Array.isArray(shared) ? shared : []);
    }).finally(() => { if (!silent) setLoading(false); });
  }, [currentWorkspace?.id]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  // D84 — Real-time push from any teammate creating/updating/deleting a note. Silent.
  useLiveInvalidate('notes', useCallback(() => fetchNotes({ silent: true }), [fetchNotes]));

  const createNote = async () => {
    if (!currentWorkspace?.id) return;
    try { const res = await noteAPI.create({ workspace_id: currentWorkspace.id, title: 'Untitled Note' }); fetchNotes(); openNote(res.data.id); } catch { toast.error('Failed'); }
  };

  const openNote = async (noteId) => {
    if (!currentWorkspace?.id) return;
    setLoadingNote(true);
    try { const res = await noteAPI.get(currentWorkspace.id, noteId); setActiveNote(res.data); setTitle(res.data.title || ''); } catch { toast.error('Failed'); } finally { setLoadingNote(false); }
  };

  const autoSave = (field, value) => {
    if (saveTimer) clearTimeout(saveTimer);
    const t = setTimeout(() => {
      if (activeNote && currentWorkspace?.id) { setSaving(true); noteAPI.update(currentWorkspace.id, activeNote.id, { [field]: value }).then(() => { setSaving(false); fetchNotes(); }).catch(() => setSaving(false)); }
    }, 800);
    setSaveTimerState(t);
  };

  const deleteNote = async (noteId) => {
    try { await noteAPI.delete(currentWorkspace.id, noteId); if (activeNote?.id === noteId) setActiveNote(null); fetchNotes(); toast.success('Deleted'); } catch {}
  };

  // ─── Recording Functions ───
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start(1000);
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;
    setTranscribing(true); setTranscript('');
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      formData.append('language', summaryLang === 'hindi' ? 'hi' : summaryLang === 'gujarati' ? 'gu' : summaryLang === 'punjabi' ? 'pa' : 'en');
      const res = await noteAPI.audioTranscribe(formData);
      setTranscript(res.data.transcript);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transcription failed');
    } finally { setTranscribing(false); }
  };

  const summarizeTranscript = async () => {
    if (!transcript) return;
    setSummarizing(true); setSummary('');
    try {
      const res = await noteAPI.transcribe({ text: transcript, language: summaryLang, workspace_id: currentWorkspace.id });
      setSummary(res.data.summary);
    } catch (err) { toast.error('Summary failed'); } finally { setSummarizing(false); }
  };

  const saveAsNote = async () => {
    if (!currentWorkspace?.id) return;
    const text = summary || transcript;
    if (!text) return;
    const content = {
      type: 'doc', content: [
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: `Meeting Notes — ${formatDateLong(new Date().toISOString())}` }] },
        ...(summary ? [
          { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: 'AI Summary' }] },
          ...summary.split('\n').filter(l => l.trim()).map(line => ({ type: 'paragraph', content: [{ type: 'text', text: line }] })),
          { type: 'horizontalRule' },
        ] : []),
        { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: 'Full Transcript' }] },
        ...transcript.split('\n').filter(l => l.trim()).map(line => ({ type: 'paragraph', content: [{ type: 'text', text: line }] })),
      ]
    };
    try {
      const res = await noteAPI.create({ workspace_id: currentWorkspace.id, title: `Meeting — ${formatDateLong(new Date().toISOString())}`, content });
      fetchNotes(); openNote(res.data.id); setShowRecorder(false);
      setAudioBlob(null); setTranscript(''); setSummary(''); setRecordingTime(0);
      toast.success('Meeting notes saved!');
    } catch { toast.error('Failed'); }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex h-full" data-testid="notes-page">
      {/* D54 B7 — Mobile: single-pane. Show rail when no active note;
          show editor when activeNote is set. md+ keeps the split-pane. */}
      <div className={`${activeNote ? 'hidden md:flex' : 'flex'} w-full md:w-[270px] border-r border-border/50 flex-col shrink-0 bg-card/30`}>
        <div className="p-3 flex items-center justify-between border-b border-border/30">
          <div className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-muted-foreground/40" /><span className="text-[13px] font-semibold">Notes</span></div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setShowRecorder(true); setAudioBlob(null); setTranscript(''); setSummary(''); }} title="Record Meeting"><Mic className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={createNote} title="New note"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        {/* D74 — My Notes / Shared tabs */}
        <div className="flex border-b border-border/30 text-[11px] font-medium" data-testid="notes-tab-strip">
          <button
            type="button"
            onClick={() => { setTab('mine'); setActiveNote(null); }}
            className={`flex-1 px-3 py-2 text-center transition-colors ${tab === 'mine' ? 'text-foreground border-b-2 border-gold' : 'text-muted-foreground/60 hover:text-foreground/70'}`}
            data-testid="notes-tab-mine"
          >
            <Lock className="h-3 w-3 inline-block mr-1 -mt-px" />
            My Notes ({notes.length})
          </button>
          <button
            type="button"
            onClick={() => { setTab('shared'); setActiveNote(null); }}
            className={`flex-1 px-3 py-2 text-center transition-colors ${tab === 'shared' ? 'text-foreground border-b-2 border-gold' : 'text-muted-foreground/60 hover:text-foreground/70'}`}
            data-testid="notes-tab-shared"
          >
            <Users className="h-3 w-3 inline-block mr-1 -mt-px" />
            Shared ({sharedNotes.length})
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground/40 px-3 py-1.5">
          {tab === 'mine'
            ? 'Your notes. Use "Share with" to let specific teammates see one.'
            : 'Notes other members have shared with you.'}
        </p>
        <ScrollArea className="flex-1">
          <div className="px-2 py-1 space-y-0.5">
            {loading ? [1, 2, 3].map((i) => <div key={i} className="h-10 bg-secondary/20 rounded animate-pulse mb-1" />) : (() => {
              const visible = tab === 'mine' ? notes : sharedNotes;
              if (visible.length === 0) {
                return (
                  <div className="text-center py-10">
                    <FileText className="h-8 w-8 text-muted-foreground/10 mx-auto mb-2" />
                    <p className="text-[12px] text-muted-foreground/40">
                      {tab === 'mine' ? 'No notes' : 'Nothing shared with you yet'}
                    </p>
                  </div>
                );
              }
              return visible.map((note) => (
                <div
                  key={note.id}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer group transition-colors ${activeNote?.id === note.id ? 'bg-foreground/[0.04]' : 'hover:bg-foreground/[0.02]'}`}
                  onClick={() => openNote(note.id)}
                  data-testid={`note-row-${note.id}`}
                >
                  <FileText className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium truncate">{note.title || 'Untitled'}</p>
                    <p className="text-[10px] text-muted-foreground/40">
                      {tab === 'shared' && note.shared_by_name && (
                        <span className="text-gold/70">Shared by {note.shared_by_name} · </span>
                      )}
                      {formatTaskDate(note.updated_at?.slice(0, 10))}
                      {tab === 'mine' && Array.isArray(note.shared_with) && note.shared_with.length > 0 && (
                        <span className="ml-1 text-gold/60">· Shared ({note.shared_with.length})</span>
                      )}
                    </p>
                  </div>
                  {tab === 'mine' && (
                    <button onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }} className="opacity-0 group-hover:opacity-100 p-0.5" data-testid={`note-delete-${note.id}`}>
                      <Trash2 className="h-3 w-3 text-muted-foreground/30 hover:text-destructive" />
                    </button>
                  )}
                </div>
              ));
            })()}
          </div>
        </ScrollArea>
      </div>

      {/* Editor — D54 B7: hidden on mobile when no active note (rail takes full width). */}
      <div className={`${activeNote ? 'flex-1' : 'hidden md:block flex-1'} overflow-y-auto`}>
        {loadingNote ? <div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : activeNote ? (
          <div className="max-w-3xl mx-auto py-6 md:py-10 px-4 md:px-8">
            {/* Mobile back-to-list button */}
            <button
              type="button"
              onClick={() => setActiveNote(null)}
              className="md:hidden flex items-center gap-1.5 text-[12px] text-muted-foreground/70 hover:text-foreground mb-4"
              data-testid="notes-mobile-back"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> All notes
            </button>
            <input value={title} onChange={e => { setTitle(e.target.value); autoSave('title', e.target.value); }} placeholder="Untitled" className="w-full text-[28px] md:text-[36px] font-display font-bold bg-transparent border-none outline-none placeholder:text-muted-foreground/20 mb-1 leading-tight" />
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground/40 mb-4 flex-wrap">
              {saving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</> : <><Clock className="h-3 w-3" /> {formatTaskDate(activeNote.updated_at?.slice(0, 10))}</>}
              {activeNote.user_id !== user?.id ? (
                <>
                  <Users className="h-3 w-3 ml-2 text-gold/70" />
                  <span className="text-gold/70">Shared with you by {activeNote.shared_by_name || 'a teammate'}</span>
                </>
              ) : (
                <>
                  {(activeNote.shared_with || []).length > 0
                    ? <><Users className="h-3 w-3 ml-2 text-gold/70" /><span className="text-gold/70">Shared with {(activeNote.shared_with || []).length}</span></>
                    : <><Lock className="h-3 w-3 ml-2" /> Private</>}
                </>
              )}
            </div>
            {/* D74 — Share control (owner-only) */}
            {activeNote.user_id === user?.id && (
              <div className="mb-5" data-testid={`note-share-bar-${activeNote.id}`}>
                <ShareWithControl
                  workspaceId={currentWorkspace?.id}
                  noteId={activeNote.id}
                  value={activeNote.shared_with || []}
                  excludeUserId={user?.id}
                  emptyLabel="Private — only you can see this"
                  onChange={async (newIds) => {
                    const res = await noteAPI.setShare(currentWorkspace.id, activeNote.id, newIds);
                    setActiveNote({ ...activeNote, shared_with: res.data.shared_with });
                    fetchNotes();
                  }}
                />
              </div>
            )}
            <NotionEditor key={activeNote.id} content={activeNote.content} onUpdate={(c) => autoSave('content', c)} placeholder="Start writing... Type '/' for blocks" workspaceId={currentWorkspace?.id} onMention={(m) => {
              // D80 Tier 2 — fire an in-app notification when a teammate
              // is @-mentioned. Backend dedupes within 6h.
              try {
                const token = localStorage.getItem('stellar_token');
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/mention-notify`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                  body: JSON.stringify({
                    workspace_id: currentWorkspace?.id,
                    mentioned_user_id: m.id,
                    note_id: activeNote.id,
                    note_title: activeNote.title || 'a note',
                    surface: 'note',
                  }),
                }).catch(() => {});
              } catch {}
            }} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileText className="h-14 w-14 text-muted-foreground/8 mx-auto mb-4" />
              <p className="text-[15px] text-muted-foreground/40">Select a note or create one</p>
              <div className="flex gap-2 mt-4 justify-center">
                <Button variant="outline" size="sm" onClick={createNote}><Plus className="h-3.5 w-3.5 mr-1.5" /> New Note</Button>
                <Button variant="outline" size="sm" onClick={() => { setShowRecorder(true); setAudioBlob(null); setTranscript(''); setSummary(''); }}><Mic className="h-3.5 w-3.5 mr-1.5" /> Record Meeting</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Meeting Recorder Dialog ─── */}
      <Dialog open={showRecorder} onOpenChange={setShowRecorder}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Mic className="h-5 w-5" /> AI Meeting Notes</DialogTitle>
            <p className="text-[12px] text-muted-foreground mt-1">Record your meeting → AI transcribes → generates summary in your language</p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Step 1: Record */}
            <div className="text-center py-4">
              {!audioBlob ? (
                <>
                  {recording ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-[14px] font-display font-bold tabular-nums">{formatTime(recordingTime)}</span>
                      </div>
                      <p className="text-[12px] text-muted-foreground">Recording... Speak into your microphone</p>
                      <Button variant="outline" size="sm" onClick={stopRecording} className="border-red-500/30 text-red-500/70">
                        <Square className="h-3.5 w-3.5 mr-1.5 fill-current" /> Stop Recording
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="h-16 w-16 rounded-full bg-foreground/[0.03] border border-border/50 flex items-center justify-center mx-auto">
                        <Mic className="h-7 w-7 text-muted-foreground/40" />
                      </div>
                      <Button onClick={startRecording}>
                        <Circle className="h-3.5 w-3.5 mr-1.5 fill-red-500 text-red-500" /> Start Recording
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-[13px] font-medium">Recording complete — {formatTime(recordingTime)}</p>
                  <audio controls src={URL.createObjectURL(audioBlob)} className="mx-auto" />
                  <Button variant="ghost" size="sm" className="text-[11px]" onClick={() => { setAudioBlob(null); setTranscript(''); setSummary(''); setRecordingTime(0); }}>Record Again</Button>
                </div>
              )}
            </div>

            {/* Step 2: Transcribe */}
            {audioBlob && !transcript && (
              <div className="flex items-center gap-3">
                <Select value={summaryLang} onValueChange={setSummaryLang}>
                  <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="hindi">Hindi</SelectItem>
                    <SelectItem value="gujarati">Gujarati</SelectItem>
                    <SelectItem value="punjabi">Punjabi</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={transcribeAudio} disabled={transcribing} className="flex-1">
                  {transcribing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Languages className="h-4 w-4 mr-2" />}
                  {transcribing ? 'Transcribing...' : 'Transcribe Audio'}
                </Button>
              </div>
            )}

            {/* Step 3: Show transcript + summarize */}
            {transcript && (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-foreground/[0.02] border border-border/50 max-h-[150px] overflow-y-auto">
                  <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider mb-1">Transcript</p>
                  <p className="text-[12px] leading-relaxed">{transcript}</p>
                </div>
                {!summary && (
                  <Button onClick={summarizeTranscript} disabled={summarizing} className="w-full">
                    {summarizing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    {summarizing ? 'Generating Summary...' : `Summarize in ${summaryLang.charAt(0).toUpperCase() + summaryLang.slice(1)}`}
                  </Button>
                )}
              </div>
            )}

            {/* Step 4: Summary */}
            {summary && (
              <div className="p-3 rounded-lg bg-foreground/[0.02] border border-gold/20 max-h-[200px] overflow-y-auto">
                <p className="text-[10px] font-semibold text-gold/60 uppercase tracking-wider mb-1">AI Summary ({summaryLang})</p>
                <div className="text-[12px] leading-relaxed whitespace-pre-wrap">{summary}</div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRecorder(false)}>Cancel</Button>
            {(transcript || summary) && <Button onClick={saveAsNote}><FileText className="h-3.5 w-3.5 mr-1.5" /> Save as Note</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
